import cn from 'clsx'
import React, { useMemo } from 'react'
import styled from 'styled-components'
import { readCSSVar, themeClassName } from 'lib/theme/themeUtils'
import { rem } from 'polished'
import { mediaQueryUp } from 'components/utils/breakpoint'

const Root = styled.div`
  box-shadow: none;
  transition: box-shadow 0.2s;
  color: ${props => props.theme.palette.neutral.default.one};

  padding: var(--pane-padding);

  ${mediaQueryUp.tablet} {
    padding: ${readCSSVar('--pane-tablet-padding', '--pane-padding')};
  }

  ${mediaQueryUp.desktop} {
    padding: ${readCSSVar('--pane-desktop-padding', '--pane-tablet-padding', '--pane-padding')};
  }

  &.bordered {
    border: 1px solid ${props => props.theme.palette.neutral.default.five};
    border-radius: ${props => props.theme.borderRadius.S};
  }

  &.background-neutral-six {
    background-color: ${props => props.theme.palette.neutral.default.six};
  }

  &.background-neutral-seven {
    background-color: ${props => props.theme.palette.neutral.default.seven};
  }

  &.background-neutral-eight {
    background-color: ${props => props.theme.palette.neutral.default.eight};
  }

  &.background-lux-plus {
    background-color: ${props => props.theme.palette.product.luxPlus.background};
    color: ${props => props.theme.palette.product.luxPlus.contrast};
  }

  &.background-tier-bronze {
    background: ${props => props.theme.palette.product.loyalty.bronze.gradientSecondaryBackground};
    background-color: ${props => props.theme.palette.product.loyalty.bronze.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.bronze.normalContrast}
  }

  &.background-tier-silver {
    background: ${props => props.theme.palette.product.loyalty.silver.gradientSecondaryBackground};
    background-color: ${props => props.theme.palette.product.loyalty.silver.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.silver.normalContrast}
  }

  &.background-tier-gold {
    background: ${props => props.theme.palette.product.loyalty.gold.gradientSecondaryBackground};
    background-color: ${props => props.theme.palette.product.loyalty.gold.lightBackground};
    color: ${props => props.theme.palette.product.loyalty.gold.normalContrast}
  }

  &.background-tier-platinum {
    background: ${props => props.theme.palette.product.loyalty.platinum.gradientSecondaryBackground};
    color: ${props => props.theme.palette.product.loyalty.platinum.darkContrast};
  }

  &.shadow {
    box-shadow: ${props => props.theme.shadow.bottom.small};
  }
`

type PanePadding = number
| `${number} ${number}`
| `${number} ${number} ${number}`
| `${number} ${number} ${number} ${number}`

interface Props extends React.ComponentProps<typeof Root> {
  /**
   * Background colour of the pane
   * @default neutral-eight
   */
  background?: 'none' | 'neutral-seven' | 'neutral-eight' | 'neutral-six' | 'lux-plus' | 'tier-bronze' | 'tier-silver' | 'tier-gold' | 'tier-platinum';
  /**
   * Adds a box-shadow to the pane, use this to make it look 'raised'
   */
  shadow?: boolean;
  /**
   * Whether or not the pane has a border
   */
  bordered?: boolean;
  /**
   * Sets the palette for the pane and it's contents at mobile and up sizes
   */
  paletteType?: App.Design.PaletteType
    /**
   * Sets the palette for the pane and it's contents at tablet and up sizes
   */
  tabletPaletteType?: App.Design.PaletteType
    /**
   * Sets the palette for the pane and it's contents at desktop and up sizes
   */
  desktopPaletteType?: App.Design.PaletteType
  /**
   * Apply padding to the pane. Same format as the CSS 'padding' value
   * e.g. 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @example 20 20
   */
  padding?: PanePadding;
  /**
   * Apply padding to the pane at tablet sizes and up. Same format as the CSS 'padding' value
   * e.g. 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @example 20 20
   */
  tabletPadding?: PanePadding;
  /**
   * Apply padding to the pane at desktop sizes and up. Same format as the CSS 'padding' value
   * e.g. 20 40 = 20 top, 20 bottom, 40 left, 40 right
   *
   * @example 20 20
   */
  desktopPadding?: PanePadding;
}

function getPadding(padding: PanePadding | undefined): string | undefined {
  if (!padding) {
    return
  }

  if (typeof padding === 'number') {
    return rem(padding)
  }

  if (typeof padding === 'string') {
    // we need to convert numbers to rem
    const parts = padding.split(' ')
    return parts.map(p => rem(+p)).join(' ')
  }
}

function Pane(props: Props) {
  const {
    children,
    shadow,
    className,
    bordered,
    paletteType,
    tabletPaletteType,
    desktopPaletteType,
    padding,
    tabletPadding,
    desktopPadding,
    background = 'neutral-eight',
    ref,
    ...rest
  } = props

  const paddingCss = useMemo(() => getPadding(padding), [padding])
  const tabletPaddingCss = useMemo(() => getPadding(tabletPadding), [tabletPadding])
  const desktopPaddingCss = useMemo(() => getPadding(desktopPadding), [desktopPadding])

  return (
    <Root
      {...rest}
      className={cn(
        className,
        themeClassName({
          base: paletteType,
          tabletAndUp: tabletPaletteType,
          // types are complicated here, but it's fine, I promise
          desktopAndUp: desktopPaletteType as any,
        }),
        `background-${background}`,
        { shadow, bordered },
      )}
      style={{
        ...rest.style,
        '--pane-padding': paddingCss,
        '--pane-tablet-padding': tabletPaddingCss,
        '--pane-desktop-padding': desktopPaddingCss,
      }}
      ref={ref}
    >
      {children}
    </Root>
  )
}

export default React.memo(Pane)
